import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Container } from "@mui/material";
import { useGlobal } from '../../utils/GlobalContext';
import { getConferenceInfo } from "../../services/ConferenceInformation";
import { getEventSessionsByEventId } from "../../services/EventInformation";
import { SessionInfo } from "../../data/SessionData";

import { ConferenceInfo, newConferenceInfo } from "../../data/conferenceInfo";
import Header from "../common/Header";
import { drawerTabs } from "../../data/admin/adminData";
import Event from "./Event";
import EventSessions from "./EventSessions";
import SocialMediaPlatform from "./SocialMediaPlatforms";
import ConferenceDescription from "./ConferenceDescription";
import MissionStatement from "./MissionStatement";
import CodeOfConduct from "./CodeOfConduct";
import Privacy from "./Privacy";
import StaffDescription from "./StaffDescription";
import MiscInfo from "./MiscInfo";
import ConferenceDetails from "./ConferenceDetails";
import { getUsers } from "../../services/UserInfo";
import { UserInfo } from "../../data/userInfo";

export default function Admin() {
  const [selectedMenuItem, setSelectedMenuItem] = useState("");
  const [conferenceInfo, setConferenceInfo] = useState<ConferenceInfo>(newConferenceInfo);
  const [eventSessions, setEventSessions] = useState<SessionInfo[]>([]);
  const [eventId, setEventId] = useState("");
  const [loadingEventSessions, setLoadingEventSessions] = useState(true); // Introduce loading state
  const { state, dispatch } = useGlobal();
  const [usersInfo, setUsersInfo] = useState<UserInfo[]>([]);
  const CurrentUserId = state.UserProfile.Id.toString();
  
  useEffect(() => {
    if (state.UserProfile.RolesList.includes("Admin")) {
      const fetchConfInfo = async () => {
        try {
          const cinfo = await getConferenceInfo();
          if (cinfo) {
            setConferenceInfo(cinfo);
          }
        } catch (err) {
          console.error("Failed to fetch Conference Information." + err);
        }
      };
      fetchConfInfo();
    }
  }, []);

  useEffect(() => {
    if (state.UserProfile.RolesList.includes("Admin")) {
      if (eventId !== "") {
        const fetchEventSessionInfo = async () => {
          try {
            const eventSessionInfo = await getEventSessionsByEventId(eventId);
            if (eventSessionInfo) {
              setEventSessions(eventSessionInfo);
            }
          } catch (err) {
            console.error("Failed to fetch Event session info for " + eventId + ": " + err);
          } finally {
            setLoadingEventSessions(false);
          }
        };
        fetchEventSessionInfo();
      }
    }
  }, [eventId]); // Depend on eventId

  useEffect(() => {
    if (CurrentUserId) {
      const fetchUserInfo = async () => {
        if (!state.UserProfile.Id.toString()) {
          return;
        }
        try {
          const cinfo = await getUsers();
          // Sort usersInfo alphabetically by name
          cinfo.sort((a, b) => a.FirstName.localeCompare(b.FirstName));
          setUsersInfo(cinfo);
        } catch (err) {
          console.error("Failed to fetch user information." + err);
        }
      };
  
      // Call fetchUserInfo only if CurrentUserId exists
      if (CurrentUserId) {
        fetchUserInfo();
      }
    }
  }, [state.UserProfile.RolesList, CurrentUserId]);



  const handleMenuItemClick = (text: string) => {
    setSelectedMenuItem(text);
  };

  const handleConferenceInfoUpdate = async () => {
    const updatedConferenceInfo = await getConferenceInfo();
    setConferenceInfo(updatedConferenceInfo);
  };

  const handleEventSessionsUpdate = async () =>{
    setLoadingEventSessions(true);
    if (eventId !== "") {     
        try {
          const eventSessionInfo = await getEventSessionsByEventId(eventId);
          if (eventSessionInfo) {
            setEventSessions(eventSessionInfo);
          }
        } catch (err) {
          console.error("Failed to fetch Event session info for " + eventId + ": " + err);
        } finally {
          setLoadingEventSessions(false); 
        } 
  }
}

  const handleViewSessionsClick = (eventId: string) => {
    setSelectedMenuItem("Event Sessions");
    setEventId(eventId);
    setLoadingEventSessions(true); // Set loading to true when reloading event sessions
  };



  const handleBackButtonClick = () => {
    setSelectedMenuItem("Events");
  };

  const functionLoading = () => {
    return (
      <Box sx={{ display: 'flex', alignContent:"center" }}>
        <CircularProgress color="info"/>
      </Box>    
    )
  }

  const renderComponent = () => {
    switch (selectedMenuItem) {
      case "Description":
        return <ConferenceDescription conferenceInfo={conferenceInfo} onConferenceInfoUpdate={handleConferenceInfoUpdate} />;
      case "Mission Statement":
        return <MissionStatement conferenceInfo={conferenceInfo} onConferenceInfoUpdate={handleConferenceInfoUpdate} />;
      case "Code of Conduct":
        return <CodeOfConduct conferenceInfo={conferenceInfo} onConferenceInfoUpdate={handleConferenceInfoUpdate} />;
      case "Privacy Statement":
        return <Privacy conferenceInfo={conferenceInfo} onConferenceInfoUpdate={handleConferenceInfoUpdate} />;
      case "Staff Description":
        return <StaffDescription conferenceInfo={conferenceInfo} onConferenceInfoUpdate={handleConferenceInfoUpdate} />;
      case "Misc Information":
        return <MiscInfo  conferenceInfo={conferenceInfo} onConferenceInfoUpdate={handleConferenceInfoUpdate} />;
      case "Events":
        return <Event onShowSessionsClick={handleViewSessionsClick}/>;
      case "Event Sessions": 
        return loadingEventSessions ? functionLoading() : <EventSessions sessionInfo={eventSessions} eventId={eventId} onEventSessionsUpdate={handleEventSessionsUpdate} onBackButtonClick={handleBackButtonClick} listUsers={usersInfo}/>;
      case "Social Media Platforms":
        return <SocialMediaPlatform />;
      default:
        return <ConferenceDetails conferenceInfo={conferenceInfo} onConferenceInfoUpdate={handleConferenceInfoUpdate} />
    }
  };

  return (
    <>
      <div className="container">
        <Container maxWidth="xl">
          <Header showDrawer={true}
            drawerTabs={drawerTabs}
            handleMenuItemClick={handleMenuItemClick}
            renderComponent={renderComponent}
          />
        </Container>
      </div>
    </>
  );
}